import React from 'react'
import { graphql } from 'gatsby'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Seo from '../components/seo'
import Layout from '../components/layout'
import LandingLayout from '../components/layouts/landing'

import pageAlternatesUrls from '../utils/pageAlternatesUrls'

function Landing({data, pageContext}) {
  const mainContent = data.mainContent
  let urls = pageAlternatesUrls(mainContent, data.alternateContent, 'slug', null, null, pageContext.locale)

  return (
    <Layout 
      frUrl={urls.frUrl}
      enUrl={urls.enUrl} 
      initLocale={pageContext.locale}
      mainTag={mainContent.mainTag.title}
      headerType="bg-white"
    >
      <Seo
        frUrl={urls.frUrl}
        enUrl={urls.enUrl}
        page={{
          title: mainContent.seoTitle ? mainContent.seoTitle : mainContent.title,
          description: mainContent.seoDescription ? mainContent.seoDescription.seoDescription : documentToPlainTextString(JSON.parse(mainContent.chapo.raw)),
          image: (mainContent.mainImage && mainContent.mainImage.file && mainContent.mainImage.file.file && mainContent.mainImage.file.file.url) ? mainContent.mainImage.file.file.url : null,
          path: (pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl
        }}
      />

      <LandingLayout 
        currentUrl={(pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl} 
        data={mainContent}
        events={data.events.nodes} 
        news={data.news.nodes} 
        socials={data.socials.nodes}
        lang={pageContext.locale}  />
    </Layout>
  );
}

export const pageQuery = graphql`
  query Landing($contentful_id: String!, $locale: String!) {
    mainContent: contentfulLanding(contentful_id: { eq: $contentful_id }, node_locale: { eq: $locale }) {
      internal {
        type
      }
      seoTitle
      seoDescription {
        seoDescription
      }
      mainImage {
        altImage
        file {
          file {
            url
          }
        }
      }
      mainTag {
        title
      }
      profile {
        title
      }
      breadcrumb {
        pageLink {
          ... on Node {
            ... on ContentfulEvent {
              title
              node_locale
              internal {
                type
              }
            } 
            ... on ContentfulNews {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHomepageSolutions {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNewsroomList {
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              title
              slug
              node_locale
              internal {
                type
              }
            }
          }
        }
        label
        customLink
        assetLink {
          file {
            url
          }
        }
      }
      breadcrumbCurrentLabel
      title
      slug
      chapo {
        raw
        references {
          __typename
          ... on ContentfulStyledText {
            contentful_id
            text
            type
          }
          ... on ContentfulNewsroomList {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulNews {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSolution {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulLanding {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulOffer {
            solution {
              title
            }
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulEvent {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulContact {
            title
            slug
            contentful_id
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSimulator {
            title
            slug
            contentful_id
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHub {
            title
            slug
            contentful_id
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHomepageSolutions {
            title
            node_locale
            contentful_id
            internal {
              type
            }
          }
          ... on ContentfulPage {
            contentful_id
            title
            slug
            customType
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulAsset {
            contentful_id
            file {
              url
            }
          }
        }
      }
      node_locale
      slices {
        ... on Node {
          ... on ContentfulSliderOffers {
            internal {
              type
            }
            offers {
              title
              internal {
                type
              }
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              node_locale
              solution {
                solution
                title
              }
              chapo {
                raw
              }
            }
          }
          ... on ContentfulFullText {
            internal {
              type
            }
            displayTitle
            title
            switchDecoPlacement
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulTestimony {
                  contentful_id
                  image {
                    altImage
                    file {
                      url
                    }
                  }
                  themeSolutions
                  imageShape
                  content {
                    content
                  }
                  author
                  job
                  theme
                }
                ... on ContentfulVideo {
                  contentful_id
                  title
                  suptitle
                  youtubeId
                  youtubePlaylistId
                  cover {
                    file {
                      url
                    }
                  }
                }
                ... on ContentfulFrontImg {
                  contentful_id
                  file {
                    url
                  }
                  altImage
                  legend {
                    legend
                  }
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  title
                  contentful_id
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  title
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            node_locale
          }
          ... on ContentfulSliceImage {
            internal {
              type
            }
            node_locale
            titleContent
            link
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSlicePdf {
            internal {
              type
            }
            node_locale
            iframe
          }
          ... on ContentfulImageTexteSection {
            internal {
              type
            }
            themeSolutions
            imageShape
            node_locale
            title
            content {
              raw
              references {
                __typename
                ... on ContentfulStyledText {
                  contentful_id
                  text
                  type
                }
                ... on ContentfulNewsroomList {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulNews {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSolution {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulLanding {
                  contentful_id
                  title
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulOffer {
                  solution {
                    title
                  }
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulEvent {
                  contentful_id
                  title
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulContact {
                  title
                  contentful_id
                  slug
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulSimulator {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHub {
                  title
                  slug
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulHomepageSolutions {
                  title
                  contentful_id
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulPage {
                  contentful_id
                  title
                  slug
                  customType
                  node_locale
                  internal {
                    type
                  }
                }
                ... on ContentfulAsset {
                  contentful_id
                  file {
                    url
                  }
                }
              }
            }
            proportions
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              customLink
              label
              assetLink {
                file {
                  url
                }
              }
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
            reversed
            theme
          }
          ... on ContentfulTabsaccordion {
            internal {
              type
            }
            type
            titleContent
            cta {
              pageLink {
                ... on Node {
                  ... on ContentfulEvent {
                    title
                    node_locale
                    internal {
                      type
                    }
                  } 
                  ... on ContentfulNews {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNewsroomList {
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                }
              }
              label
              customLink
              assetLink {
                file {
                  url
                }
              }
            }
            items {
              title
              subtitle1
              subtitle2
              content {
                raw
                references {
                  __typename
                  ... on ContentfulStyledText {
                    contentful_id
                    text
                    type
                  }
                  ... on ContentfulFrontImg {
                    contentful_id
                    file {
                      url
                    }
                    altImage
                    legend {
                      legend
                    }
                  }
                  ... on ContentfulNewsroomList {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulNews {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSolution {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulLanding {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulOffer {
                    solution {
                      title
                    }
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulEvent {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulContact {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulSimulator {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHub {
                    contentful_id
                    title
                    slug
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulHomepageSolutions {
                    contentful_id
                    title
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    contentful_id
                    title
                    slug
                    customType
                    node_locale
                    internal {
                      type
                    }
                  }
                  ... on ContentfulAsset {
                    contentful_id
                    file {
                      url
                    }
                  }
                }
              }
              cta {
                pageLink {
                  ... on Node {
                    ... on ContentfulEvent {
                      title
                      node_locale
                      internal {
                        type
                      }
                    } 
                    ... on ContentfulNews {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSolution {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulLanding {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulOffer {
                      solution {
                        title
                      }
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHomepageSolutions {
                      title
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulContact {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulSimulator {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulHub {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulPage {
                      title
                      slug
                      customType
                      node_locale
                      internal {
                        type
                      }
                    }
                    ... on ContentfulNewsroomList {
                      title
                      slug
                      node_locale
                      internal {
                        type
                      }
                    }
                  }
                }
                label
                customLink
                assetLink {
                  file {
                    url
                  }
                }
              }
              contactItems {
                title
                icon
                link {
                  pageLink {
                    ... on Node {
                      ... on ContentfulEvent {
                        title
                        node_locale
                        internal {
                          type
                        }
                      } 
                      ... on ContentfulNews {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulSolution {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulLanding {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulOffer {
                        solution {
                          title
                        }
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHomepageSolutions {
                        title
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulContact {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulSimulator {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulHub {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulPage {
                        title
                        slug
                        customType
                        node_locale
                        internal {
                          type
                        }
                      }
                      ... on ContentfulNewsroomList {
                        title
                        slug
                        node_locale
                        internal {
                          type
                        }
                      }
                    }
                  }
                  customLink
                  label
                  assetLink {
                    file {
                      url
                    }
                  }
                }
                content {
                  raw
                }
              }
            }
          }
          ... on ContentfulVideo {
            internal {
              type
            }
            title
            suptitle
            youtubeId
            youtubePlaylistId
            cover {
              file {
                url
              }
            }
          }
          ... on ContentfulTestimony {
            internal {
              type
            }
            themeSolutions
            imageShape
            author
            job
            theme
            content {
              content
            }
            image {
              altImage
              file {
                file {
                  url
                }
              }
            }
          }
          ... on ContentfulSliderPartnersClients {
            internal {
              type
            }
            title
            items {
              logo {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
              link
              noFollow
            }
          }
          ... on ContentfulSlider {
            internal {
              type
            }
            title
            type
            chapo {
              chapo
            }
            items {
              ... on Node {
                ... on ContentfulFrontImg {
                  altImage
                  file {
                    file {
                      url
                    }
                  }
                  legend:childContentfulFrontImgLegendTextNode {
                    legend
                  }
                }
                ... on ContentfulShortItem {
                  title
                  text {
                    text
                  }
                }
              }
            }
          }
        }
      }
      pushSolutions {
        title
        internal {
          type
        }
        solution
        node_locale
        mainImage {
          altImage
          file {
            file {
              url
              details {
                image {
                  height
                  width
                }
              }
            }
          }
        }
      }
    }

    alternateContent: contentfulLanding(contentful_id: { eq: $contentful_id }, node_locale: { ne: $locale }, title: {ne: null}) {
      title
      slug
      node_locale
    }

    events: allContentfulEvent(limit: 3, sort: {order: ASC, fields: startDate}, filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, fields: {isFuture: {eq: true}}, node_locale: {eq: $locale}, title: {ne: null}}) {
      nodes {
        title
        node_locale
        id
        internal {
          type
        }
        mainImage {
          altImage
          file {
            file {
              url
              details {
                image {
                  height
                  width
                }
              }
            }
          }
        }
        startDate
        endDate
      }
    }

    socials: allSocials(limit: 9, sort: {order: DESC, fields: external_created_at }, filter: {source: {source: {ne: "Facebook"}}, message: {ne: "dummy"}}) {
      nodes {
        message
        external_created_at
        source {
          source
          name
          term
        }
        full_url
      }
    }

    news: allContentfulNews(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale}, title: {ne: null}}, limit: 9, sort: {fields: publicationDate, order: DESC}) {
      nodes {
        title
        internal {
          type
        }
        node_locale
        publicationDate
        createdAt
        mainImage {
          altImage
          file {
            file {
              url
              details {
                image {
                  height
                  width
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default Landing;